import React from 'react';
import classNames from 'classnames';
import { Form as FinalForm } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import { required } from '../../../util/validators';

import { FieldRadioButton, FieldTextInput, Form, Modal, Button } from '../../../components';

import css from './CancelOrderModal.module.css';

// Values are saved to the transaction's protectedData.cancelReason, so keep them stable.
export const CANCEL_REASONS = [
  'out-of-stock',
  'damaged',
  'buyer-requested',
  'cant-ship-in-time',
  'other',
];
export const CANCEL_REASON_OTHER = 'other';

const CancelOrderForm = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        handleSubmit,
        intl,
        invalid,
        values,
        cancelSubmitted,
        cancelError,
        cancelInProgress,
        onKeepOrder,
      } = fieldRenderProps;

      const isOther = values?.cancelReason === CANCEL_REASON_OTHER;
      const submitDisabled = invalid || cancelInProgress || cancelSubmitted;

      return (
        <Form className={css.formRoot} onSubmit={handleSubmit}>
          <fieldset className={css.reasons}>
            <legend className={css.reasonsLegend}>
              <FormattedMessage id="CancelOrderModal.reasonLabel" />
            </legend>
            {CANCEL_REASONS.map(reason => (
              <FieldRadioButton
                key={reason}
                id={`cancelReason.${reason}`}
                name="cancelReason"
                value={reason}
                label={intl.formatMessage({ id: `CancelOrderModal.reason.${reason}` })}
              />
            ))}
          </fieldset>
          {isOther ? (
            <FieldTextInput
              className={css.note}
              type="textarea"
              id="cancelReasonNote"
              name="cancelReasonNote"
              label={intl.formatMessage({ id: 'CancelOrderModal.noteLabel' })}
              validate={required(intl.formatMessage({ id: 'CancelOrderModal.noteRequired' }))}
            />
          ) : null}
          <p className={css.refundNote}>
            <FormattedMessage id="CancelOrderModal.refundNote" />
          </p>
          <p className={css.errorPlaceholder}>
            {cancelError ? <FormattedMessage id="CancelOrderModal.cancelFailed" /> : null}
          </p>
          <Button
            className={css.submitButton}
            type="submit"
            inProgress={cancelInProgress}
            disabled={submitDisabled}
            ready={cancelSubmitted}
          >
            <FormattedMessage id="CancelOrderModal.submit" />
          </Button>
          <button type="button" className={css.keepButton} onClick={onKeepOrder}>
            <FormattedMessage id="CancelOrderModal.keepOrder" />
          </button>
        </Form>
      );
    }}
  />
);

const validate = values =>
  values.cancelReason ? {} : { cancelReason: 'CancelOrderModal.reasonRequired' };

/**
 * Lets the seller cancel an unshipped order and tell us why.
 *
 * @component
 * @param {Object} props
 * @param {string} props.id - The id
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onCloseModal - Closes the modal
 * @param {Function} props.onManageDisableScrolling - The on manage disable scrolling function
 * @param {Function} props.onCancelOrder - Called with { cancelReason, cancelReasonNote }
 * @param {boolean} props.cancelSubmitted - Whether the order has been canceled
 * @param {boolean} props.cancelInProgress - Whether the cancel is in progress
 * @param {Object} [props.cancelError] - The cancel error
 * @returns {JSX.Element}
 */
const CancelOrderModal = props => {
  const intl = useIntl();
  const {
    className,
    rootClassName,
    id,
    isOpen = false,
    onCloseModal,
    onManageDisableScrolling,
    onCancelOrder,
    focusElementId,
    cancelSubmitted = false,
    cancelInProgress = false,
    cancelError,
  } = props;
  const classes = classNames(rootClassName || css.root, className);

  return (
    <Modal
      id={id}
      containerClassName={classes}
      contentClassName={css.modalContent}
      isOpen={isOpen}
      onClose={onCloseModal}
      onManageDisableScrolling={onManageDisableScrolling}
      focusElementId={focusElementId}
      usePortal
      closeButtonMessage={intl.formatMessage({ id: 'CancelOrderModal.close' })}
    >
      {cancelSubmitted ? (
        <>
          <p className={css.modalTitle}>
            <FormattedMessage id="CancelOrderModal.sentTitle" />
          </p>
          <p className={css.modalMessage}>
            <FormattedMessage id="CancelOrderModal.sentMessage" />
          </p>
        </>
      ) : (
        <>
          <p className={css.modalTitle}>
            <FormattedMessage id="CancelOrderModal.title" />
          </p>
          <CancelOrderForm
            onSubmit={onCancelOrder}
            validate={validate}
            cancelSubmitted={cancelSubmitted}
            cancelInProgress={cancelInProgress}
            cancelError={cancelError}
            onKeepOrder={onCloseModal}
            intl={intl}
          />
        </>
      )}
    </Modal>
  );
};

export default CancelOrderModal;
